import { PrineConvertAdapterOptions } from '../types/PrineConvertAdapterOptions';
import { PrineConvertAdapter } from '../PrineConvertAdapter';
import { PrineConvertAdapterExport } from '../PrineConvertAdapterExport';
import { PrineConvertExportData } from '../types/PrineConvertExportData';
import { PrineConvertImportData } from '../types/PrineConvertImportData';
import { PrineConvertAdapterImport } from '../types/PrineConvertAdapterImport';

export interface PrineConvertAdapterHTMLOptions extends PrineConvertAdapterOptions {
    style?: string;
    stylesheet?: string;
    htmlPre?: string;
    htmlPost?: string;
    columnStyles?: string[];
}

export class PrineConvertAdapterHTML extends PrineConvertAdapter {
    options: PrineConvertAdapterHTMLOptions = {
        mimeType: 'text/html',
        style: `
table.prine-convert-adapter-html {
  width: calc(100% - 2rem);
  margin: 1rem;
  border-collapse:collapse;
  page-break-inside: auto;
}

table.prine-convert-adapter-html thead tr th {
  background-color: #333;
  color: #fff;
}

table.prine-convert-adapter-html tbody tr td {
  padding: 0.4rem 0.4rem;
  border: 1px solid #777;
}

table.prine-convert-adapter-html thead tr th {
  padding: 0.3rem 0.3rem;
  border: 1px solid #777;
}

table.prine-convert-adapter-html tr {
  page-break-inside: avoid;
  page-break-after: auto;
}

table.prine-convert-adapter-html thead {
  display: table-header-group;
}
        `,
    };

    casts = {
        null: () => '',
        bool: (value: any) => (value ? '✔' : ''),
        array: (value: any) => value.join(', '),
        object: (value: any) =>
            Object.entries(value)
                .map(([key, _]) => `${key}: ${_}`)
                .join('; '),
    };

    constructor(options?: Partial<PrineConvertAdapterHTMLOptions>) {
        super();
        this.options = { ...this.options, ...options };

        if (options && options.casts) {
            this.casts = { ...this.casts, ...options.casts };
        }
    }

    export(data: PrineConvertExportData): PrineConvertAdapterExport[] {
        const options = this.options;

        let indexedFieldTypes: any[] = [];

        if (data.length > 0 && data[0].rows.length > 0) {
            indexedFieldTypes = data[0].rows[0].map((cell, index) => {
                return this.getType(data[0], index, cell);
            });
        }

        return data
            .map((container) => this.castValues(container))
            .map((container) => {
                const fields = container.fields.map((field, index) => {
                    const type = indexedFieldTypes[index];
                    const label = this.getFieldLabel(field);
                    const style =
                        this.options.columnStyles && this.options.columnStyles[index]
                            ? ` style="${this.options.columnStyles[index]}"`
                            : '';

                    return `<th data-type="${type}" data-key="${label}"${style}>${label}</th>`;
                });

                const rows = container.rows.map((row) => {
                    const cells = row
                        .map((cell, index) => {
                            const type = indexedFieldTypes[index];
                            const key = this.getFieldLabel(container.fields[index]);
                            const style =
                                this.options.columnStyles && this.options.columnStyles[index]
                                    ? ` style="${this.options.columnStyles[index]}"`
                                    : '';

                            return `<td data-type="${type}" data-key="${key}"${style}>${cell}</td>`;
                        })
                        .join('');

                    return `<tr>${cells}</tr>`;
                });

                return (
                    (this.options.htmlPre ? this.options.htmlPre : '') +
                    '<table class="prine-convert-adapter-html">' +
                    '<thead>' +
                    '<tr>' +
                    fields.join('') +
                    '</tr>' +
                    '</thead>' +
                    '<tbody>' +
                    rows.join('') +
                    '</tbody>' +
                    '</table>' +
                    (this.options.style
                        ? '<style>' + this.options.style.trim().replaceAll('\n', '') + '</style>'
                        : '') +
                    (this.options.stylesheet ? '<link rel="stylesheet" href="' + this.options.stylesheet + '">' : '') +
                    (this.options.htmlPost ? this.options.htmlPost : '')
                );
            })
            .map((html) => {
                return new (class extends PrineConvertAdapterExport {
                    constructor() {
                        super(options);
                    }

                    asRaw() {
                        return html;
                    }
                })();
            });
    }

    import(data: PrineConvertImportData): PrineConvertAdapterImport[] {
        return [];
    }
}
